"use client";

/**
 * CRM Settings Tab for Email Agents
 *
 * Uses the CRM Abstraction Layer:
 * - Supports multiple CRM providers (Kommo, HubSpot, etc.)
 * - Uses crm_bindings for per-agent configuration
 * - Provider-agnostic UI that adapts to provider capabilities
 */

import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Link2,
  Loader2,
  HelpCircle,
  AlertCircle,
  ArrowRight,
  Filter,
  UserX,
  CheckCircle2,
  XCircle,
  Unplug,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCrmBinding, type CrmStage } from "@/hooks/integrations/use-crm-binding";
import { useEmailAgentConfig } from "@/hooks/agents/email";
import { useEmailClassifierConfigQuery } from "@/hooks/queries/email-classifier-config";
import { useCrmIntegrations } from "@/hooks/integrations/use-crm-integrations";
import type { CrmProviderType } from "@/lib/crm/types";
import { AddCrmIntegrationDialog, CrmProviderLogo } from "@/components/crm";
import { HandoverPipelineSelector } from "@/components/agents/email/handover-pipeline-selector";
import { PageLoader } from "@/components/ui/page-loader";
import { usePermissions } from "@/hooks/use-permissions";
import { CRM_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






interface CrmSettingsProps {
  agentId: string;
}

export function CrmSettings({ agentId }: CrmSettingsProps) {



  const tUi = useTranslations("Email.AgentsEmailTabsCrmSettings")

  const {
    binding,
    integrations,
    pipelines,
    selectedIntegration,
    isLoading,
    isLoadingPipelines,
    pipelinesError,
    isSaving,
    error,
    loadBinding: _loadBinding,
    loadIntegrations,
    saveBinding,
    updateBindingConfig,
    getStagesForPipeline,
    // Tenant default info
    hasTenantDefault,
    tenantDefaultIntegration,
  } = useCrmBinding({
    scopeType: "email_agent",
    scopeId: agentId,
    autoLoad: true,
  });

  const { can } = usePermissions();

  // Also load agent config for classifier labels and human_handover settings
  const {
    config: agentConfig,
    isLoading: isLoadingConfig,
    loadConfig,
    updateHumanHandover: _updateHumanHandover,
  } = useEmailAgentConfig(agentId);

  // Classifier categories live on the scoped AI-config chain, not in agent.config
  const { data: classifierConfig } = useEmailClassifierConfigQuery(agentId);

  // CRM integrations hook for adding new integrations
  const {
    providers,
    isConnecting,
    connectIntegration,
    prepareOAuthInstall,
    error: crmError,
    clearError: clearCrmError,
    refreshIntegrations: refreshCrmIntegrations,
  } = useCrmIntegrations({ autoLoad: true });

  // Local state
  const [pipelineStages, setPipelineStages] = useState<CrmStage[]>([]);
  const [handoverStages, setHandoverStages] = useState<CrmStage[]>([]);
  const [connectionTestResult, setConnectionTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showAddCrmDialog, setShowAddCrmDialog] = useState(false);

  useEffect(() => {
    loadConfig();
    loadIntegrations();
  }, [loadConfig, loadIntegrations]);

  // Load stages when pipeline changes
  useEffect(() => {
    if (binding?.config?.pipeline_id) {
      const stages = getStagesForPipeline(binding.config.pipeline_id);
      setPipelineStages(stages);
    } else {
      setPipelineStages([]);
    }
  }, [binding?.config?.pipeline_id, pipelines, getStagesForPipeline]);

  // Load handover stages when handover pipeline changes
  useEffect(() => {
    if (binding?.config?.handover?.pipeline_id) {
      const stages = getStagesForPipeline(binding.config.handover.pipeline_id);
      setHandoverStages(stages);
    } else {
      setHandoverStages([]);
    }
  }, [binding?.config?.handover?.pipeline_id, pipelines, getStagesForPipeline]);

  // Computed: check if CRM is actually enabled (binding exists AND enabled !== false)
  const isCrmEnabled = binding && binding.config?.enabled !== false;

  // Handlers
  const handleIntegrationChange = async (integrationId: string) => {
    setConnectionTestResult(null);

    if (integrationId === "none") {
      // Disable CRM for this agent (override tenant default)
      // We need to create/update a binding with enabled=false
      if (binding) {
        // Update existing binding to disable
        await updateBindingConfig({ enabled: false });
      } else if (integrations.length > 0) {
        // No binding exists - create one with enabled=false to override tenant default
        // Use first available integration as placeholder (won't be used since disabled)
        await saveBinding(integrations[0].id, {
          enabled: false,
          pipeline_id: undefined,
          stage_mapping: {},
        });
      }
      return;
    }

    // Create/update binding with new integration
    await saveBinding(integrationId, {
      enabled: true,
      pipeline_id: undefined,
      stage_mapping: {},
    });
  };

  const handlePipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      pipeline_id: pipelineId,
      stage_mapping: {},
    });
  };

  const handleCategoryMappingChange = async (category: string, stageId: string) => {
    const currentMapping = binding?.config?.stage_mapping || {};
    const newMapping = {
      ...currentMapping,
      [category]: stageId === "none" ? null : stageId,
    };
    await updateBindingConfig({ stage_mapping: newMapping });
  };

  const handleNoteToggle = async (createNote: boolean) => {
    await updateBindingConfig({ create_note_on_reply: createNote });
  };

  // Email Sorting enable/disable handler
  const handleSortingToggle = async (enabled: boolean) => {
    if (enabled) {
      await updateBindingConfig({ sorting_enabled: true });
    } else {
      // Clear all sorting-related config when disabled
      await updateBindingConfig({
        sorting_enabled: false,
        pipeline_id: undefined,
        stage_mapping: {},
        create_note_on_reply: false,
      });
    }
  };

  // Human escalation handlers
  const handleHandoverPipelineChange = async (pipelineId: string) => {
    await updateBindingConfig({
      handover: {
        ...binding?.config?.handover,
        pipeline_id: pipelineId,
        stage_id: undefined,
      },
    });
  };

  const handleHandoverStatusChange = async (statusId: string) => {
    await updateBindingConfig({
      handover: {
        ...binding?.config?.handover,
        stage_id: statusId,
      },
    });
  };

  // Categories come from the scoped classifier config (AI-config chain)
  const categories = classifierConfig?.classifier_labels ?? [];

  // Filter active stages (not archived)
  const activeStages = pipelineStages.filter((s) => !s.isArchived);

  const getQualificationColor = (label: string) => {
    const colors: Record<string, string> = {
      "Qualified Lead": "bg-green-100 text-green-800 border-green-200",
      "Support Request": "bg-blue-100 text-blue-800 border-blue-200",
      Spam: "bg-red-100 text-red-800 border-red-200",
      Other: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[label] || "bg-purple-100 text-purple-800 border-purple-200";
  };

  const getProviderDisplayName = (provider: string) => {
    const names: Record<string, string> = {
      kommo: "Kommo",
      hubspot: "HubSpot",
      salesforce: "Salesforce",
      pipedrive: "Pipedrive",
    };
    return names[provider] || provider;
  };

  // Handler for connecting new CRM
  const handleConnectCrm = useCallback(
    async (
      provider: string,
      name: string,
      credentials: Record<string, unknown>,
      metadata?: Record<string, unknown>
    ) => {
      const result = await connectIntegration(provider as CrmProviderType, name, credentials, metadata);
      if (result.success) {
        // Refresh integrations list after successful connection
        await loadIntegrations();
        await refreshCrmIntegrations();
      }
      return result;
    },
    [connectIntegration, loadIntegrations, refreshCrmIntegrations]
  );

  // Get provider info for an integration
  const getProviderInfo = useCallback(
    (providerId: string) => {
      return providers.find((p) => p.id === providerId);
    },
    [providers]
  );

  // Get terminology for the selected integration (or default)
  const terminology = selectedIntegration
    ? getProviderInfo(selectedIntegration.provider)?.terminology
    : null;

  // Default terminology for CRM-neutral context
  const defaultTerminology = {
    opportunity: 'opportunity',
    opportunityPlural: 'opportunities',
    pipeline: 'pipeline',
    stage: 'stage',
  };

  // Use provider-specific terminology if available, otherwise default
  const t = terminology || defaultTerminology;

  if (isLoading || isLoadingConfig) {
    return <PageLoader message={tUi("loadingCRMSettings")} />;
  }

  const bindingConfig = binding?.config || {};

  // For backwards compatibility: if sorting_enabled is undefined but pipeline_id exists,
  // treat as enabled (legacy bindings that were created before sorting_enabled field)
  const isSortingEnabled = bindingConfig.sorting_enabled ?? !!bindingConfig.pipeline_id;

  if (!can(CRM_PERMISSIONS.VIEW)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><span key={tUi("accessDenied")}>{tUi("accessDenied")}</span></CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              <span key={tUi("youDonTHavePermissionToAccessCRM")}>{tUi("youDonTHavePermissionToAccessCRM")}</span></AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* CRM Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            <span><span key={tUi("cRMIntegration")}>{tUi("cRMIntegration")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("connectThisAgentToYourCRMToSync")}>{tUi("connectThisAgentToYourCRMToSync")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {integrations.length === 0 ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    <span key={tUi("noCRMIntegrationsAvailableConnectACRMTo")}>{tUi("noCRMIntegrationsAvailableConnectACRMTo")}</span></span>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowAddCrmDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                <span><span key={tUi("connectCRM")}>{tUi("connectCRM")}</span></span>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium"><span key={tUi("cRMConnection")}>{tUi("cRMConnection")}</span></Label>
                <Select
                  value={isCrmEnabled ? binding?.integrationId : "none"}
                  onValueChange={handleIntegrationChange}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("selectACRMIntegration")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <Unplug className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500"><span key={tUi("noneDisabled")}>{tUi("noneDisabled")}</span></span>
                      </div>
                    </SelectItem>
                    {integrations.map((integration) => {
                      const providerInfo = getProviderInfo(integration.provider);
                      return (
                        <SelectItem key={integration.id} value={integration.id}>
                          <div className="flex items-center gap-2">
                            {providerInfo?.logoUrl ? (
                              <CrmProviderLogo
                                provider={integration.provider}
                                logoUrl={providerInfo.logoUrl}
                                size={16}
                              />
                            ) : (
                              <div
                                className={cn(
                                  "w-4 h-4 rounded-full",
                                  integration.isActive ? "bg-green-500" : "bg-gray-300"
                                )}
                              />
                            )}
                            <span>
                              {integration.name}{" "}
                              <span className="text-gray-400">
                                ({getProviderDisplayName(integration.provider)})
                              </span>
                            </span>
                            {!integration.isActive && (
                              <Badge variant="secondary" className="text-xs ml-1">
                                <span key={tUi("disabled")}>{tUi("disabled")}</span></Badge>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  <span key={tUi("selectNoneToDisableCRMForThisAgent")}>{tUi("selectNoneToDisableCRMForThisAgent")}</span></p>
              </div>

              {/* Add another CRM button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddCrmDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                <span><span key={tUi("orAddADifferentCRM")}>{tUi("orAddADifferentCRM")}</span></span>
              </Button>

              {/* Connection Status - only show when CRM is enabled */}
              {isCrmEnabled && selectedIntegration && !pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800"><TranslationText>{tUi.rich("connectedToValue0Value1", { value0: String(selectedIntegration.name), value1: String(getProviderDisplayName(selectedIntegration.provider)) })}</TranslationText></span>
                </div>
              )}

              {pipelinesError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800">{pipelinesError}</span>
                </div>
              )}

              {/* Tenant Default Inheritance Notice - show when no agent binding but tenant default exists */}
              {!binding && hasTenantDefault && tenantDefaultIntegration && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800"><TranslationText>{tUi.rich("usingTenantDefaultValue0Value1SelectASpecific", { value0: String(tenantDefaultIntegration.name), value1: String(getProviderDisplayName(tenantDefaultIntegration.provider)) })}</TranslationText></span>
                </div>
              )}

              {connectionTestResult && (
                <div
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg",
                    connectionTestResult.success
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  )}
                >
                  {connectionTestResult.success ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span
                    className={cn(
                      "text-sm",
                      connectionTestResult.success ? "text-green-800" : "text-red-800"
                    )}
                  >
                    {connectionTestResult.message}
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Sorting - Pipeline & Category Mapping (only if CRM is enabled) */}
      {isCrmEnabled && selectedIntegration && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  <span><span key={tUi("emailSorting")}>{tUi("emailSorting")}</span></span>
                </CardTitle>
                <CardDescription className="mt-1.5">
                  <span><span key={tUi("sortEmailsIntoYourCRM")}>{tUi("sortEmailsIntoYourCRM")}</span></span> {t.pipeline.toLowerCase()} <span><span key={tUi("and")}>{tUi("and")}</span></span> {t.stage.toLowerCase()}<span><span key={tUi("sBasedOnClassification")}>{tUi("sBasedOnClassification")}</span></span>
                </CardDescription>
              </div>
              <Switch
                checked={isSortingEnabled}
                onCheckedChange={handleSortingToggle}
                disabled={isSaving}
              />
            </div>
          </CardHeader>
          <CardContent className={cn("space-y-6", !isSortingEnabled && "hidden")}>
            {/* Pipeline Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t.pipeline}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t.pipeline} <span><span key={tUi("whereNew")}>{tUi("whereNew")}</span></span> {t.opportunityPlural.toLowerCase()} <span><span key={tUi("willBeCreated")}>{tUi("willBeCreated")}</span></span></p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              {isLoadingPipelines ? (
                <div className="flex items-center gap-2 p-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-gray-500"><span key={tUi("loadingPipelines")}>{tUi("loadingPipelines")}</span></span>
                </div>
              ) : pipelines.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm text-yellow-800">
                      <span key={tUi("noPipelinesFoundPleaseCheckYourCRMConfiguration")}>{tUi("noPipelinesFoundPleaseCheckYourCRMConfiguration")}</span></span>
                  </div>
                </div>
              ) : (
                <Select
                  value={bindingConfig.pipeline_id || ""}
                  onValueChange={handlePipelineChange}
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("selectAPipeline")} />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelines.map((pipeline) => (
                      <SelectItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name} <span>{pipeline.isDefault && "(Default)"}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Category to Stage Mapping - only show when pipeline is selected */}
            {bindingConfig.pipeline_id && (
              <>
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium mb-3 block">
                    <span><span key={tUi("categoryTo")}>{tUi("categoryTo")}</span></span> {t.stage} <span><span key={tUi("mapping")}>{tUi("mapping")}</span></span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p><span><span key={tUi("mapEmailCategoriesToSpecific")}>{tUi("mapEmailCategoriesToSpecific")}</span></span> {t.stage.toLowerCase()}<span><span key={tUi("sInYour")}>{tUi("sInYour")}</span></span> {t.pipeline.toLowerCase()}</p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>

                  {categories.length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800">
                          <span key={tUi("noCategoriesConfiguredPleaseSetUpClassificationCategories")}>{tUi("noCategoriesConfiguredPleaseSetUpClassificationCategories")}</span></span>
                      </div>
                    </div>
                  ) : activeStages.length === 0 ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-800"><span key={tUi("noActiveStagesFound")}>{tUi("noActiveStagesFound")}</span></span>
                      </div>
                      <p key={pipelineStages.length > 0
                          ? tUi("foundValue0StagesButNoneAreActive", { value0: String(pipelineStages.length) })
                          : tUi("noStagesFoundForTheSelectedPipeline")} className="text-sm text-yellow-700">
                        {pipelineStages.length > 0
                          ? tUi("foundValue0StagesButNoneAreActive", { value0: String(pipelineStages.length) })
                          : tUi("noStagesFoundForTheSelectedPipeline")}
                      </p>
                    </div>
                  ) : (
                    <>
                      {pipelineStages.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                          <Filter className="w-3 h-3" />
                          <span><TranslationText>{tUi.rich("showingValue0OfValue1StagesActiveOnly", { value0: String(activeStages.length), value1: String(pipelineStages.length) })}</TranslationText></span>
                        </div>
                      )}

                      <div className="space-y-3">
                        {categories.map((category) => {
                          const mappingValue = bindingConfig.stage_mapping?.[category];
                          const selectedValue =
                            mappingValue === null || mappingValue === undefined || mappingValue === ""
                              ? "none"
                              : mappingValue;

                          return (
                            <div key={category} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-40 flex-shrink-0">
                                <Badge className={cn("text-xs font-medium", getQualificationColor(category))}>
                                  {category}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-gray-400">
                                <ArrowRight className="w-4 h-4" />
                              </div>
                              <div className="flex-1">
                                <Select
                                  value={selectedValue}
                                  onValueChange={(value) => handleCategoryMappingChange(category, value)}
                                  disabled={isSaving}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder={tUi("selectStage")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">
                                      <span className="text-gray-500"><span key={tUi("noMapping")}>{tUi("noMapping")}</span></span>
                                    </SelectItem>
                                    {activeStages.map((stage) => (
                                      <SelectItem key={stage.id} value={stage.id}>
                                        <div className="flex items-center gap-2">
                                          <div
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: stage.color || "#gray" }}
                                          />
                                          <span>{stage.name}</span>
                                          <span className="text-xs text-gray-400 ml-1">(#{stage.order})</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-blue-800">
                          <div className="font-medium mb-1"><span key={tUi("mappingSummary")}>{tUi("mappingSummary")}</span></div>
                          <div className="space-y-1">
                            <div>
                              {Object.keys(bindingConfig.stage_mapping || {}).filter(
                                (key) => bindingConfig.stage_mapping?.[key]
                              ).length}{" "}
                              <span><span key={tUi("of")}>{tUi("of")}</span></span> {categories.length} <span><span key={tUi("categoriesMappedTo")}>{tUi("categoriesMappedTo")}</span></span> {activeStages.length} <span><span key={tUi("activeStages")}>{tUi("activeStages")}</span></span>
                            </div>
                            {pipelineStages.length > activeStages.length && (
                              <div className="text-xs text-blue-600">
                                <Filter className="w-3 h-3 inline mr-1" />
                                {pipelineStages.length - activeStages.length} <span><span key={tUi("inactiveStagesHidden")}>{tUi("inactiveStagesHidden")}</span></span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Note Creation - inside Email Sorting */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200">
                    <div>
                      <Label htmlFor="note-toggle" className="text-sm font-medium text-gray-700 cursor-pointer">
                        <span key={tUi("createCRMNoteOnAutoReply")}>{tUi("createCRMNoteOnAutoReply")}</span></Label>
                      <p className="text-xs text-gray-500">
                        <span><span key={tUi("addANoteToThe")}>{tUi("addANoteToThe")}</span></span> {t.opportunity.toLowerCase()} <span><span key={tUi("whenAnAutoReplyIsSent")}>{tUi("whenAnAutoReplyIsSent")}</span></span>
                      </p>
                    </div>
                    <Switch
                      id="note-toggle"
                      checked={bindingConfig.create_note_on_reply || false}
                      onCheckedChange={handleNoteToggle}
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Human escalation CRM settings */}
      {isCrmEnabled && selectedIntegration && agentConfig.human_handover?.enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5 text-red-500" />
              <span><span key={tUi("humanEscalation")}>{tUi("humanEscalation")}</span></span>
            </CardTitle>
            <CardDescription>
              <span><span key={tUi("configureWhere")}>{tUi("configureWhere")}</span></span> {t.opportunityPlural.toLowerCase()} <span><span key={tUi("goWhenAIHandsOverToHumanSupport")}>{tUi("goWhenAIHandsOverToHumanSupport")}</span></span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HandoverPipelineSelector
              pipelines={pipelines}
              stages={handoverStages}
              selectedPipelineId={bindingConfig.handover?.pipeline_id}
              selectedStageId={bindingConfig.handover?.stage_id}
              isLoadingPipelines={isLoadingPipelines}
              isSaving={isSaving}
              showWarning={agentConfig.human_handover?.enabled}
              onPipelineChange={handleHandoverPipelineChange}
              onStageChange={handleHandoverStatusChange}
            />
          </CardContent>
        </Card>
      )}

      {/* Add CRM Integration Dialog */}
      <AddCrmIntegrationDialog
        open={showAddCrmDialog}
        onOpenChange={setShowAddCrmDialog}
        providers={providers}
        onConnect={handleConnectCrm}
        onPrepareOAuth={prepareOAuthInstall}
        isConnecting={isConnecting}
        error={crmError}
        onClearError={clearCrmError}
      />
    </div>
  );
}
